import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { Credentials } from "../storage/credentials";
import { imageSource, JellyfinItem } from "../api/jellyfin";
import { colors, interaction, motion, radius, systemSansFont } from "../theme/tokens";
import { CardHoverOverlay, formatRuntime } from "./CardHoverOverlay";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import { FocusablePressable } from "./FocusablePressable";
import { MediaImage } from "./MediaImage";
import { usePlayedStateToggle } from "../utils/usePlayedStateToggle";
import { focusRegisteredNode, getFocusModality, subscribeFocusModality, subscribeFocusTransitions } from "../tvNavigation/registry";
import { isTelevisionRuntime } from "../utils/platformCapabilities";
import { AnimeLocalization, getAnimeLocalization } from "../utils/animeLocalization";

type Props = {
  item: JellyfinItem;
  credentials: Credentials;
  width: number;
  onPress: () => void;
  onPlay?: () => void;
  hideLabels?: boolean;
  showTypeChip?: boolean;
  onFocusChange?: (focused: boolean) => void;
  hovered?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  gap?: number;
  variant?: "default" | "search";
  instantImageReveal?: boolean;
  focusGroup?: string;
  focusDestinations?: import('../tvNavigation/core').FocusDestinations;
  animeLocalization?: AnimeLocalization;
};

function MediaCardComponent({
  item,
  credentials,
  width,
  onPress,
  onPlay,
  hideLabels = false,
  showTypeChip = false,
  onFocusChange,
  hovered: controlledHovered,
  onHoverChange,
  gap,
  variant = "default",
  instantImageReveal = false,
  focusGroup,
  focusDestinations,
  animeLocalization: localizationOverride,
}: Props) {
  const playedState = usePlayedStateToggle(item, credentials);
  const animeLocalization = localizationOverride ?? getAnimeLocalization(item);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [overlayHovered, setOverlayHovered] = useState(false);
  const [controllerActionMode, setControllerActionMode] = useState(false);
  const controllerEntryAt = useRef(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const hoverExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (hoverExitTimer.current) clearTimeout(hoverExitTimer.current);
    },
    [],
  );
  useEffect(() => {
    setImageFailed(false);
    setImageReady(false);
  }, [item.Id, variant]);
  const cardFocusId = `${focusGroup ?? 'cards'}.${item.Id ?? item.Name ?? 'unknown'}`;
  useEffect(() => subscribeFocusModality((next) => {
    if (next === 'pointer') setControllerActionMode(false);
  }), []);
  useEffect(() => {
    if (!controllerActionMode) return undefined;
    return subscribeFocusTransitions((transition) => {
    if (!controllerActionMode || !transition.toId) return;
    if (transition.toId === cardFocusId && transition.fromId !== cardFocusId && transition.reason === 'back') {
      setControllerActionMode(false);
      return;
    }
    if (transition.toId !== cardFocusId && !transition.toId.startsWith(`${cardFocusId}.`)) setControllerActionMode(false);
    });
  }, [cardFocusId, controllerActionMode]);
  const enterCard = () => {
    if (hoverExitTimer.current) clearTimeout(hoverExitTimer.current);
    setHovered(true);
    onHoverChange?.(true);
  };
  const leaveCard = () => {
    setHovered(false);
    hoverExitTimer.current = setTimeout(() => {
      setOverlayHovered(false);
      onHoverChange?.(false);
    }, 70);
  };
  const interactionSuppressed = controlledHovered === false;
  const activeHovered =
    !interactionSuppressed && (hovered || controlledHovered === true);
  const cardProgress = useRef(new Animated.Value(0)).current;
  const cardActive =
    controllerActionMode || (!interactionSuppressed && (activeHovered || overlayHovered || focused));
  const focusPlayAction = (attempt = 0) => {
    requestAnimationFrame(() => {
      if (focusRegisteredNode(`${cardFocusId}.play`) || attempt >= 5) return;
      focusPlayAction(attempt + 1);
    });
  };
  const handleControllerCardPress = () => {
    controllerEntryAt.current = Date.now();
    setControllerActionMode(true);
    // Let the native Pressable finish dispatching this DPAD_CENTER event
    // before moving focus into the overlay. Otherwise Android TV can deliver
    // the same event to the newly focused Play action as well.
    setTimeout(() => focusPlayAction(), 120);
  };
  const handleCardPress = () => {
    // On native TV, the platform Pressable also emits onPress for OK. Treat
    // that as entering the card action layer; otherwise it bypasses the
    // overlay and invokes the card's desktop details action directly.
    if ((!isTelevisionRuntime() && getFocusModality() === 'pointer') || !onPlay || variant === 'search') {
      onPress();
      return;
    }
    handleControllerCardPress();
  };
  const handleCardPlay = () => {
    // Android TV can deliver the same DPAD_CENTER event to the card and the
    // newly focused Play affordance. Keep that entry event from immediately
    // launching playback; a subsequent press activates Play normally.
    if (Date.now() - controllerEntryAt.current < 300) return;
    setControllerActionMode(false);
    (onPlay ?? onPress)();
  };
  const handleCardInfo = () => { setControllerActionMode(false); onPress(); };
  useEffect(() => {
    cardProgress.stopAnimation();
    const animation = Animated.timing(cardProgress, {
      toValue: cardActive ? 1 : 0,
      duration: motion.interaction,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    animation.start();
    return () => animation.stop();
  }, [cardActive, cardProgress]);
  const handleOverlayHover = (value: boolean) => {
    if (value) {
      setOverlayHovered(true);
      enterCard();
    }
  };
  const height = Math.round(
    width * (variant === "search" && width >= 190 ? 1.49 : 1.5),
  );
  const cardScale = cardProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.035],
  });
  const cardTranslateY = cardProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -4],
  });
  const genres = (
    item.Genres ??
    item.GenreItems?.map((genre) => genre.Name).filter(
      (genre): genre is string => Boolean(genre),
    ) ??
    []
  )
    .slice(0, 2)
    .join(" / ");
  const typeLabel = item.Type === "Series" ? "TV Show" : (item.Type ?? "");
  const detail = [
    item.ProductionYear ? String(item.ProductionYear) : "",
    formatRuntime(item.RunTimeTicks),
    genres || typeLabel,
  ]
    .filter(Boolean)
    .join(" • ");
  const artworkSource = useMemo(() => item.Id && !imageFailed
    ? imageSource(
      credentials,
      item,
      "Primary",
      // TV density is generally 1x at 1080p. Requesting 2x artwork for every
      // mounted card needlessly doubles decoded bitmap memory on low-RAM TVs.
      Math.min(420, Math.round(width * (isTelevisionRuntime() ? 1 : 2))),
      Math.min(630, Math.round(height * (isTelevisionRuntime() ? 1 : 2))),
      82,
    )
    : undefined, [credentials.accessToken, credentials.serverUrl, height, imageFailed, item.Id, width]);
  return (
    <Animated.View
      style={[
        styles.animatedRoot,
        { marginRight: gap ?? (width <= 110 ? 6 : 8) },
        activeHovered && styles.hovered,
        focused && styles.focused,
      ]}
    >
      <FocusablePressable
        testID={`media-card-${item.Id ?? item.Name ?? "unknown"}`}
        focusId={cardFocusId}
        focusGroup={focusGroup}
        focusDestinations={focusDestinations}
        focusRole="card"
        focusHalo={false}
        onPress={handleCardPress}
        onControllerPress={onPlay && variant !== 'search' ? handleControllerCardPress : undefined}
        onHoverIn={enterCard}
        onHoverOut={leaveCard}
        onFocus={() => {
          setFocused(true);
          onFocusChange?.(true);
        }}
        onBlur={() => {
          setFocused(false);
          onFocusChange?.(false);
        }}
        style={[styles.root, { width, paddingBottom: hideLabels ? 0 : 9 }]}
      >
        <View style={{ width, height, overflow: "visible" }}>
        <Animated.View
          style={[
            styles.imageShell,
            {
              position: "absolute",
              width,
              height,
              left: 0,
              top: 0,
              transform: [{ translateY: cardTranslateY }, { scale: cardScale }],
            },
            cardActive && styles.imageShellHovered,
        focused && !controllerActionMode && styles.imageShellFocused,
          ]}
        >
          {item.Id && !imageFailed ? (
            <MediaImage
              source={artworkSource}
              fallbackLabel={item.Name}
              reduceMotion={instantImageReveal}
              onReady={() => {
                setImageReady(true);
              }}
              onError={() => setImageFailed(true)}
              resizeMode="cover"
              style={StyleSheet.absoluteFillObject}
            />
          ) : (
            <View style={styles.imageFallback}>
              <Text style={styles.imageFallbackText}>
                {(item.Name ?? "Untitled").slice(0, 1).toUpperCase()}
              </Text>
            </View>
          )}
          {animeLocalization ? <View pointerEvents="none" style={styles.animeLocalizationBadge}><Text style={styles.animeLocalizationText}>{animeLocalization}</Text></View> : null}
          {variant === "search" && imageReady ? (
            <View pointerEvents="none" style={styles.searchChips}>
              <Text style={styles.searchTypeChip}>
                {typeLabel.toUpperCase()}
              </Text>
              {item.ProductionYear ? (
                <Text style={styles.searchYearChip}>{item.ProductionYear}</Text>
              ) : null}
            </View>
          ) : null}
          {typeof item.UserData?.PlayedPercentage === "number" &&
          item.UserData.PlayedPercentage > 0 ? (
            <View style={styles.progressTrack}>
              <View
                style={[
                  styles.progress,
                  {
                    width: Math.round(
                      (width * Math.min(item.UserData.PlayedPercentage, 100)) /
                        100,
                    ),
                  },
                ]}
              />
            </View>
          ) : null}
          {variant === "search" ? cardActive ? <><View testID="search-card-hover-overlay" pointerEvents="none" style={StyleSheet.absoluteFillObject}>
            <Svg width={width} height={height} style={StyleSheet.absoluteFillObject}>
              <Defs><LinearGradient id="search-card-fade" x1="0" y1="0" x2="0" y2="1"><Stop offset="0.2" stopColor="#000" stopOpacity="0" /><Stop offset="1" stopColor="#000" stopOpacity="0.78" /></LinearGradient></Defs>
              <Rect width={width} height={height} fill="url(#search-card-fade)" />
            </Svg>
            <View style={styles.searchDetailsButton}><Text style={styles.searchDetailsText}>View details</Text></View>
          </View>{playedState.supported ? <CardHoverOverlay
              item={item}
              credentials={credentials}
              visible={cardActive}
              focusId={cardFocusId}
              focusGroup={focusGroup}
              cardFocusDestinations={focusDestinations}
              controllerActionMode={controllerActionMode}
              width={width}
              height={height}
              controlsOnly
              played={playedState.played}
              playedPending={playedState.pending}
              onHoverChange={handleOverlayHover}
              onPlay={handleCardPlay}
              onInfo={handleCardInfo}
              onTogglePlayed={() => { void playedState.togglePlayed(); }}
            /> : null}</> : null : <CardHoverOverlay
              item={item}
              credentials={credentials}
              visible={cardActive}
              focusId={cardFocusId}
              focusGroup={focusGroup}
              cardFocusDestinations={focusDestinations}
              controllerActionMode={controllerActionMode}
              width={width}
              height={height}
              showTypeChip={showTypeChip}
              onHoverChange={handleOverlayHover}
              onPlay={handleCardPlay}
              onInfo={handleCardInfo}
              played={playedState.played}
              playedPending={playedState.pending}
              onTogglePlayed={playedState.supported ? () => { void playedState.togglePlayed(); } : undefined}
            />}
        </Animated.View>
        </View>
        {!hideLabels ? (
          <>
            <Text
              numberOfLines={1}
              style={[
                styles.title,
                variant === "search" && styles.searchTitle,
                variant === "search" &&
                  width < 190 &&
                  styles.searchTitleCompact,
              ]}
            >
              {item.Name ?? "Untitled"}
            </Text>
            <Text
              numberOfLines={1}
              style={[
                styles.detail,
                variant === "search" && styles.searchDetail,
              ]}
            >
              {variant === "search"
                ? `${typeLabel} • ${item.ProductionYear ?? ""}`
                : detail}
            </Text>
          </>
        ) : null}
      </FocusablePressable>
    </Animated.View>
  );
}

export const MediaCard = React.memo(MediaCardComponent, (previous, next) =>
  previous.item === next.item &&
  previous.credentials.accessToken === next.credentials.accessToken &&
  previous.credentials.serverUrl === next.credentials.serverUrl &&
  previous.width === next.width &&
  previous.hideLabels === next.hideLabels &&
  previous.showTypeChip === next.showTypeChip &&
  previous.hovered === next.hovered &&
  previous.gap === next.gap &&
  previous.variant === next.variant &&
  previous.instantImageReveal === next.instantImageReveal
);

const styles = StyleSheet.create({
  animatedRoot: { borderRadius: radius.medium },
  root: {},
  hovered: { zIndex: 4 },
  focused: { zIndex: 5 },
  imageShell: {
    overflow: "hidden",
    borderRadius: radius.medium,
    backgroundColor: colors.surfaceRaised,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  imageShellFocused: { borderWidth: interaction.focusBorderWidth, borderColor: colors.brandBright, shadowColor: colors.brand, shadowOpacity: 0.62, shadowRadius: 10, elevation: interaction.raisedElevation },
  imageShellHovered: {
    borderColor: "rgba(229,9,20,0.34)",
    shadowColor: "#000",
    shadowOpacity: 0.62,
    shadowRadius: 18,
    elevation: interaction.raisedElevation,
  },
  searchChips: {
    position: "absolute",
    top: 10,
    left: 10,
    right: 10,
    zIndex: 5,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  searchTypeChip: {
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: "rgba(7,7,10,0.82)",
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 13,
    fontWeight: "700",
  },
  animeLocalizationBadge: {
    position: "absolute", top: 8, left: 8, paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: 5, backgroundColor: "rgba(12, 12, 14, 0.86)", borderWidth: 1,
    borderColor: "rgba(255,255,255,0.28)", zIndex: 2,
  },
  animeLocalizationText: { color: colors.text, fontFamily: systemSansFont, fontSize: 11, fontWeight: "800", letterSpacing: 0.8 },
  searchYearChip: {
    paddingHorizontal: 7,
    paddingVertical: 5,
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: "rgba(7,7,10,0.82)",
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 13,
    fontWeight: "700",
  },
  searchDetailsButton: {
    position: "absolute",
    left: 12,
    bottom: 12,
    minHeight: 32,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand,
    shadowColor: colors.brand,
    shadowOpacity: 0.34,
    shadowRadius: 14,
    elevation: 7,
  },
  searchDetailsText: { color: colors.text, fontFamily: systemSansFont, fontSize: 12, lineHeight: 16, fontWeight: "900" },
  imageFallback: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surfaceRaised,
  },
  imageFallbackText: {
    color: "rgba(255,255,255,0.28)",
    fontSize: 20,
    fontWeight: "900",
  },
  title: {
    color: colors.textStrong,
    marginTop: 6,
    fontSize: 13,
    fontWeight: "500",
  },
  detail: { color: colors.textMuted, marginTop: 2, fontSize: 11 },
  searchTitle: {
    marginTop: 6,
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "700",
  },
  searchTitleCompact: { marginTop: 7 },
  searchDetail: { marginTop: 3, fontFamily: systemSansFont, fontSize: 13, lineHeight: 16 },
  progressTrack: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 4,
    zIndex: 8,
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  progress: { height: 4, backgroundColor: colors.brand },
});
